import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Bed as BedIcon, Plus, Edit, Trash2, X } from 'lucide-react';
import { bedService, wardService } from '../../lib/services.ipd';
import type { Bed, Ward } from '../../lib/services.ipd';
import toast from 'react-hot-toast';

interface BedFormData {
  ward_id: string;
  bed_no: string;
  bed_type: 'general' | 'semi_private' | 'private' | 'icu' | 'nicu' | 'emergency';
  daily_rate: number;
  status: 'available' | 'occupied' | 'cleaning' | 'maintenance' | 'reserved';
}

interface WardFormData {
  ward_no: string;
  name: string;
  department?: string;
  floor?: string;
  bed_capacity: number;
}

export default function BedManagement() {
  const [beds, setBeds] = useState<Bed[]>([]);
  const [wards, setWards] = useState<Ward[]>([]);
  const [selectedWard, setSelectedWard] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  
  const [showAddBedModal, setShowAddBedModal] = useState(false);
  const [showEditBedModal, setShowEditBedModal] = useState(false);
  const [showAddWardModal, setShowAddWardModal] = useState(false);
  const [editingBed, setEditingBed] = useState<Bed | null>(null);
  const [retireConfirm, setRetireConfirm] = useState<string | null>(null);

  const {
    register: registerBed,
    handleSubmit: handleSubmitBed,
    reset: resetBed,
    formState: { errors: bedErrors, isSubmitting: isSubmittingBed },
  } = useForm<BedFormData>();

  const {
    register: registerWard,
    handleSubmit: handleSubmitWard,
    reset: resetWard,
    formState: { errors: wardErrors, isSubmitting: isSubmittingWard },
  } = useForm<WardFormData>();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [bedsData, wardsData] = await Promise.all([
        bedService.getAll(),
        wardService.getAll(),
      ]);
      setBeds(bedsData);
      setWards(wardsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load bed data');
    } finally {
      setLoading(false);
    }
  };

  const onSubmitBed = async (data: BedFormData) => {
    try {
      const isUnique = await bedService.checkBedNoUnique(
        data.bed_no,
        data.ward_id,
        editingBed?.id
      );
      
      if (!isUnique) {
        toast.error('Bed number already exists in this ward');
        return;
      }

      if (data.daily_rate < 0) {
        toast.error('Daily rate cannot be negative');
        return;
      }

      if (editingBed) {
        await bedService.update(editingBed.id, data);
        toast.success('Bed updated successfully');
      } else {
        await bedService.create({ ...data, active: true });
        toast.success('Bed created successfully');
      }
      
      setShowAddBedModal(false);
      setShowEditBedModal(false);
      setEditingBed(null);
      resetBed();
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Operation failed');
    }
  };

  const onSubmitWard = async (data: WardFormData) => {
    try {
      await wardService.create({ ...data, active: true });
      toast.success('Ward created successfully');
      setShowAddWardModal(false);
      resetWard();
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create ward');
    }
  };

  const handleEditBed = (bed: Bed) => {
    setEditingBed(bed);
    resetBed({
      ward_id: bed.ward_id,
      bed_no: bed.bed_no,
      bed_type: bed.bed_type,
      daily_rate: bed.daily_rate,
      status: bed.status,
    });
    setShowEditBedModal(true);
  };

  const handleRetireBed = async (id: string) => {
    try {
      await bedService.retire(id);
      toast.success('Bed retired successfully');
      setRetireConfirm(null);
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to retire bed');
    }
  };

  const openAddBedModal = () => {
    setEditingBed(null);
    resetBed({ daily_rate: 0, status: 'available' });
    setShowAddBedModal(true);
  };

  const openAddWardModal = () => {
    resetWard({ bed_capacity: 0 });
    setShowAddWardModal(true);
  };

  const filteredBeds = selectedWard === 'all' 
    ? beds 
    : beds.filter(b => b.ward_id === selectedWard);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      available: 'bg-green-100 text-green-800',
      occupied: 'bg-red-100 text-red-800',
      cleaning: 'bg-yellow-100 text-yellow-800',
      maintenance: 'bg-gray-100 text-gray-800',
      reserved: 'bg-blue-100 text-blue-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bed Management</h1>
          <p className="text-gray-600">View and manage hospital bed availability</p>
        </div>
        <button
          onClick={openAddBedModal}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Add Bed
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Ward</label>
            <select
              value={selectedWard}
              onChange={(e) => setSelectedWard(e.target.value)}
              className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="all">All Wards</option>
              {wards.map(ward => (
                <option key={ward.id} value={ward.id}>{ward.name}</option>
              ))}
            </select>
          </div>
          <div className="mt-6">
            <button
              onClick={openAddWardModal}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              <Plus className="w-4 h-4" />
              Add Ward
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredBeds.map(bed => (
          <div key={bed.id} className="bg-white rounded-lg shadow-sm p-4 border-2 border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <BedIcon className="w-5 h-5 text-gray-400" />
                <span className="font-semibold">{bed.bed_no}</span>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(bed.status)}`}>
                {bed.status}
              </span>
            </div>
            <div className="text-sm space-y-1 mb-3">
              <p className="text-gray-600">{bed.ward?.name}</p>
              <p className="capitalize text-gray-600">{bed.bed_type.replace('_', ' ')}</p>
              <p className="font-medium">₹{bed.daily_rate}/day</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleEditBed(bed)}
                className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
              >
                <Edit className="w-3 h-3" />
                Edit
              </button>
              <button
                onClick={() => setRetireConfirm(bed.id)}
                className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded hover:bg-red-100"
              >
                <Trash2 className="w-3 h-3" />
                Retire
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredBeds.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No beds found
        </div>
      )}

      {showAddBedModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Add Bed</h3>
              <button
                onClick={() => setShowAddBedModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmitBed(onSubmitBed)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Ward *</label>
                <select
                  {...registerBed('ward_id', { required: 'Ward is required' })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select ward</option>
                  {wards.map(ward => (
                    <option key={ward.id} value={ward.id}>{ward.name}</option>
                  ))}
                </select>
                {bedErrors.ward_id && <p className="mt-1 text-sm text-red-600">{bedErrors.ward_id.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Bed Number *</label>
                <input
                  {...registerBed('bed_no', { required: 'Bed number is required' })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., W101-01"
                />
                {bedErrors.bed_no && <p className="mt-1 text-sm text-red-600">{bedErrors.bed_no.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Bed Type *</label>
                <select
                  {...registerBed('bed_type', { required: 'Bed type is required' })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select type</option>
                  <option value="general">General</option>
                  <option value="semi_private">Semi Private</option>
                  <option value="private">Private</option>
                  <option value="icu">ICU</option>
                  <option value="nicu">NICU</option>
                  <option value="emergency">Emergency</option>
                </select>
                {bedErrors.bed_type && <p className="mt-1 text-sm text-red-600">{bedErrors.bed_type.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Daily Rate *</label>
                <input
                  {...registerBed('daily_rate', { 
                    required: 'Daily rate is required',
                    valueAsNumber: true,
                    min: { value: 0, message: 'Rate cannot be negative' }
                  })}
                  type="number"
                  step="0.01"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
                {bedErrors.daily_rate && <p className="mt-1 text-sm text-red-600">{bedErrors.daily_rate.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status *</label>
                <select
                  {...registerBed('status', { required: 'Status is required' })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="available">Available</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="cleaning">Cleaning</option>
                  <option value="reserved">Reserved</option>
                </select>
                {bedErrors.status && <p className="mt-1 text-sm text-red-600">{bedErrors.status.message}</p>}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddBedModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingBed}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                >
                  {isSubmittingBed ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditBedModal && editingBed && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Bed</h3>
              <button
                onClick={() => setShowEditBedModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmitBed(onSubmitBed)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Ward</label>
                <input
                  value={editingBed.ward?.name}
                  disabled
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Bed Number</label>
                <input
                  value={editingBed.bed_no}
                  disabled
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Bed Type *</label>
                <select
                  {...registerBed('bed_type', { required: 'Bed type is required' })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="general">General</option>
                  <option value="semi_private">Semi Private</option>
                  <option value="private">Private</option>
                  <option value="icu">ICU</option>
                  <option value="nicu">NICU</option>
                  <option value="emergency">Emergency</option>
                </select>
                {bedErrors.bed_type && <p className="mt-1 text-sm text-red-600">{bedErrors.bed_type.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Daily Rate *</label>
                <input
                  {...registerBed('daily_rate', { 
                    required: 'Daily rate is required',
                    valueAsNumber: true,
                    min: { value: 0, message: 'Rate cannot be negative' }
                  })}
                  type="number"
                  step="0.01"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
                {bedErrors.daily_rate && <p className="mt-1 text-sm text-red-600">{bedErrors.daily_rate.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status *</label>
                <select
                  {...registerBed('status', { required: 'Status is required' })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="available">Available</option>
                  <option value="occupied">Occupied</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="cleaning">Cleaning</option>
                  <option value="reserved">Reserved</option>
                </select>
                {bedErrors.status && <p className="mt-1 text-sm text-red-600">{bedErrors.status.message}</p>}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditBedModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingBed}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                >
                  {isSubmittingBed ? 'Updating...' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddWardModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Add Ward</h3>
              <button
                onClick={() => setShowAddWardModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmitWard(onSubmitWard)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Ward Code *</label>
                <input
                  {...registerWard('ward_no', { required: 'Ward code is required' })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., W101"
                />
                {wardErrors.ward_no && <p className="mt-1 text-sm text-red-600">{wardErrors.ward_no.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Ward Name *</label>
                <input
                  {...registerWard('name', { required: 'Ward name is required' })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., General Ward A"
                />
                {wardErrors.name && <p className="mt-1 text-sm text-red-600">{wardErrors.name.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Floor</label>
                <input
                  {...registerWard('floor')}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 1st Floor"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Department</label>
                <input
                  {...registerWard('department')}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Medicine"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Bed Capacity</label>
                <input
                  {...registerWard('bed_capacity', { valueAsNumber: true })}
                  type="number"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddWardModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingWard}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50"
                >
                  {isSubmittingWard ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {retireConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Retire Bed</h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to retire this bed? This action will make the bed inactive and it will no longer appear in available bed filters.
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setRetireConfirm(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRetireBed(retireConfirm)}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                >
                  Confirm Retire
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
